import { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import './BillingModal.css';

const BillingModal = memo(({ show, onClose }) => {
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoPaymentEnabled, setAutoPaymentEnabled] = useState(true);
  const [updating, setUpdating] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      if (!billingData) fetchBillingData();
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, [show]);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBillingData(data);
        setAutoPaymentEnabled(data.subscription?.autoPaymentEnabled ?? true);
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoPayment = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/toggle-auto-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ enabled: !autoPaymentEnabled })
      });

      if (response.ok) {
        const next = !autoPaymentEnabled;
        setAutoPaymentEnabled(next);
        setBillingData(prev => prev ? {
          ...prev,
          subscription: { ...prev.subscription, autoPaymentEnabled: next }
        } : prev);
      }
    } catch (error) {
      console.error('Error toggling auto-payment:', error);
      alert('Failed to update auto-payment setting');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of the current billing period.')) {
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Subscription cancelled successfully');
        setBillingData(prev => prev ? {
          ...prev,
          subscription: { ...prev.subscription, status: 'cancelling' }
        } : prev);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription');
    } finally {
      setUpdating(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/reactivate-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Subscription reactivated successfully');
        setBillingData(prev => prev ? {
          ...prev,
          subscription: { ...prev.subscription, status: 'active' }
        } : prev);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to reactivate subscription');
      }
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      alert('Failed to reactivate subscription');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/update-payment-method`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portalUrl;
      }
    } catch (error) {
      console.error('Error opening payment portal:', error);
      alert('Failed to open payment portal');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'active': return 'status-active';
      case 'cancelled': return 'status-cancelled';
      case 'cancelling': return 'status-warning';
      case 'past_due': return 'status-past-due';
      default: return '';
    }
  };

  if (!show) return null;

  return createPortal(
    <div className="billing-modal-overlay" onClick={onClose}>
      <div className="billing-modal-shell" onClick={(e) => e.stopPropagation()}>
        <button className="billing-close-btn" onClick={onClose}>×</button>
        <div className="billing-modal-scroll">

          <h2 className="billing-title">Billing & Subscription</h2>

          {loading ? (
            <div className="billing-loading">
              <div className="spinner"></div>
              <p>Loading billing information...</p>
            </div>
          ) : billingData ? (
            <div className="billing-content">
              <div className="billing-section">
                <h3>Current Subscription</h3>
                {billingData.subscription?.plan ? (
                  <div className="subscription-info">
                    <div className="info-row">
                      <span className="info-label">Plan:</span>
                      <span className="info-value">{billingData.subscription.plan}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Status:</span>
                      <span className={`status-badge ${getStatusBadgeClass(billingData.subscription.status)}`}>
                        {billingData.subscription.status}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Start Date:</span>
                      <span className="info-value">{formatDate(billingData.subscription.startDate)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">End Date:</span>
                      <span className="info-value">{formatDate(billingData.subscription.endDate)}</span>
                    </div>

                    <div className="info-row auto-payment-row">
                      <span className="info-label">Auto-Payment:</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={autoPaymentEnabled}
                          onChange={handleToggleAutoPayment}
                          disabled={updating}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="toggle-label">
                        {autoPaymentEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>

                    <div className="subscription-actions">
                      <button
                        className="action-btn secondary"
                        onClick={handleUpdatePaymentMethod}
                        disabled={updating}
                      >
                        Update Payment Method
                      </button>

                      {billingData.subscription.status === 'active' ? (
                        <button
                          className="action-btn danger"
                          onClick={handleCancelSubscription}
                          disabled={updating}
                        >
                          Cancel Subscription
                        </button>
                      ) : billingData.subscription.status === 'cancelling' ? (
                        <button
                          className="action-btn primary"
                          onClick={handleReactivateSubscription}
                          disabled={updating}
                        >
                          Reactivate Subscription
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <p className="no-subscription">No active subscription</p>
                )}
              </div>

              {billingData.upcomingInvoice && (
                <div className="billing-section">
                  <h3>Upcoming Payment</h3>
                  <div className="upcoming-invoice">
                    <div className="invoice-amount">
                      ${parseFloat(billingData.upcomingInvoice.amount || 0).toFixed(2)}
                    </div>
                    <div className="invoice-date">
                      Due on {formatDate(billingData.upcomingInvoice.dueDate)}
                    </div>
                  </div>
                </div>
              )}

              <div className="billing-section">
                <h3>Payment History</h3>
                {billingData.paymentHistory && billingData.paymentHistory.length > 0 ? (
                  <div className="payment-history-list">
                    {billingData.paymentHistory.map((payment) => (
                      <div key={payment.id} className="payment-history-item">
                        <div className="payment-info">
                          <div className="payment-date">
                            {formatDate(payment.created_at)}
                          </div>
                          <div className="payment-details">
                            <span className="payment-plan">{payment.plan_type}</span>
                            <span className={`payment-status status-${payment.payment_status}`}>
                              {payment.payment_status}
                            </span>
                          </div>
                        </div>
                        <div className="payment-amount">
                          ${parseFloat(payment.amount || 0).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-history">No payment history available</p>
                )}
              </div>
            </div>
          ) : (
            <div className="billing-error">
              <p>Failed to load billing information</p>
              <button onClick={fetchBillingData}>Retry</button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
});

export default BillingModal;
